import { type Ref, ref, computed } from 'vue'
import { isImage, isSlide } from '@collection-player/utils/slide-type.util'
import type { CollectionSlide } from '@collection-player/types/collection-player.type'
import type { useAnnotationLayer } from '@collection-player/composables/annotation-layer.use.ts'
import type { MarkerArea } from '@markerjs/markerjs3'

const MAX_ZOOM = 2.0
const MIN_ZOOM = 0.4
const ZOOM_THRESHOLD = 0.2
const INITIAL_ZOOM = 1

const getCanvasContainer = (marker: MarkerArea) => {
  // @ts-expect-error access private property
  return marker._canvasContainer
}

export default (annotationLayer: ReturnType<typeof useAnnotationLayer>, activeSlide: Ref<CollectionSlide | null>) => {
  const zoom = ref(INITIAL_ZOOM)

  const isZoomEnabled = computed(() => activeSlide.value && (isImage(activeSlide.value) || isSlide(activeSlide.value)))

  const resetZoom = () => {
    if (!annotationLayer.activeMarker.value) return

    const canvasContainer = getCanvasContainer(annotationLayer.activeMarker.value)
    const { scrollLeft, scrollTop } = canvasContainer

    annotationLayer.activeMarker.value.zoomLevel = zoom.value = INITIAL_ZOOM

    canvasContainer.scrollTo({
      left: scrollLeft,
      top: scrollTop,
    })
  }

  const zoomIn = () => {
    if (!annotationLayer.activeMarker.value) return

    const canvasContainer = getCanvasContainer(annotationLayer.activeMarker.value)
    const currentScale = annotationLayer.activeMarker.value.zoomLevel ?? INITIAL_ZOOM
    const { scrollLeft, scrollTop } = canvasContainer

    annotationLayer.activeMarker.value.zoomLevel = zoom.value = Math.min(currentScale + ZOOM_THRESHOLD, MAX_ZOOM)

    canvasContainer.scrollTo({
      left: scrollLeft,
      top: scrollTop,
    })
  }

  const zoomOut = () => {
    if (!annotationLayer.activeMarker.value) return

    const canvasContainer = getCanvasContainer(annotationLayer.activeMarker.value)
    const currentScale = annotationLayer.activeMarker.value.zoomLevel ?? INITIAL_ZOOM
    const { scrollLeft, scrollTop } = canvasContainer

    annotationLayer.activeMarker.value.zoomLevel = zoom.value = Math.max(currentScale - ZOOM_THRESHOLD, MIN_ZOOM)

    canvasContainer.scrollTo({
      left: scrollLeft,
      top: scrollTop,
    })
  }

  // keep the function for compatibility, but it does nothing
  const onZoomChangeEvent = () => {}

  return {
    zoom,
    isZoomEnabled,
    resetZoom,
    zoomIn,
    zoomOut,
    onZoomChangeEvent,
  }
}
